import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Car, LogOut, Loader2, MapPin, CheckCircle2, AlertTriangle, Clock, Globe } from "lucide-react";
import { toast } from "sonner";
import { SaudiPlate, PLATE_ALLOWED_LETTERS } from "../components/SaudiPlate";
import { CAR_BRANDS, CAR_COLORS, colorHex, brandLogo } from "../components/carBrands";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    title: "Valet Parking",
    center: "مركز لوسنت لطب الأسنان",
    enterCode: "أدخل رمز الدخول الخاص بك لتسجيل الحضور",
    codeShort: "أدخل رمز الدخول المكون من 4 أرقام",
    checkin: "تسجيل الحضور",
    checkinFail: "فشل تسجيل الدخول",
    welcome: (n) => `مرحباً ${n}`,
    geoHint: "يتطلب التسجيل السماح بالوصول لموقعك والتواجد داخل محيط المركز",
    presentSince: "حاضر منذ",
    checkout: "انصراف",
    checkoutDone: "تم تسجيل الانصراف — يومك سعيد",
    checkoutFail: "فشل تسجيل الانصراف",
    logCarTitle: "تسجيل سيارة",
    carType: "نوع السيارة *",
    otherBrand: "أخرى",
    otherBrandPlaceholder: "اكتب نوع السيارة",
    color: "اللون",
    plateLabel: "لوحة السيارة (3 أحرف و 4 أرقام)",
    plateInvalid: "اللوحة يجب أن تكون 3 أحرف و 4 أرقام",
    carTypeRequired: "نوع السيارة مطلوب",
    carSaved: "تم تسجيل السيارة",
    carFail: "فشل تسجيل السيارة",
    shiftCars: (n) => `سيارات هذه الوردية (${n})`,
    langBtn: "English",
  },
  en: {
    title: "Valet Parking",
    center: "Lucent Dental Center",
    enterCode: "Enter your access code to check in",
    codeShort: "Enter your 4-digit access code",
    checkin: "Check In",
    checkinFail: "Check-in failed",
    welcome: (n) => `Welcome ${n}`,
    geoHint: "Requires location access and being within the center's perimeter",
    presentSince: "Checked in at",
    checkout: "Check Out",
    checkoutDone: "Checked out — have a great day",
    checkoutFail: "Check-out failed",
    logCarTitle: "Log a Car",
    carType: "Car type *",
    otherBrand: "Other",
    otherBrandPlaceholder: "Type the car brand",
    color: "Color",
    plateLabel: "Plate (3 letters & 4 digits)",
    plateInvalid: "Plate must be 3 letters & 4 digits",
    carTypeRequired: "Car type is required",
    carSaved: "Car logged",
    carFail: "Failed to log car",
    shiftCars: (n) => `This shift's cars (${n})`,
    langBtn: "العربية",
  },
};

const getPosition = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

export default function ValetPublic() {
  const [lang, setLang] = useState("ar");
  const t = T[lang];
  const isRTL = lang === "ar";
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(isRTL ? "ar-SA" : "en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [carForm, setCarForm] = useState({ car_type: "", color: "", plateLetters: "", plateNumbers: "" });
  const [otherBrand, setOtherBrand] = useState(false);
  const [savingCar, setSavingCar] = useState(false);
  const [brands, setBrands] = useState(CAR_BRANDS);

  useEffect(() => {
    axios.get(`${API}/valet/public/brands`).then((r) => r.data?.length && setBrands(r.data)).catch(() => {});
  }, []);

  const checkin = async () => {
    if (code.trim().length < 4) { setError(t.codeShort); return; }
    setBusy(true); setError("");
    const loc = await getPosition();
    try {
      const res = await axios.post(`${API}/valet/public/checkin`, { code: code.trim(), ...loc });
      setSession(res.data);
      toast.success(t.welcome(res.data.driver.name));
    } catch (e) {
      setError(e.response?.data?.detail || t.checkinFail);
    } finally { setBusy(false); }
  };

  const checkout = async () => {
    setBusy(true);
    const loc = await getPosition();
    try {
      await axios.post(`${API}/valet/public/checkout`, { code: code.trim(), ...loc });
      toast.success(t.checkoutDone);
      setSession(null); setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.checkoutFail);
    } finally { setBusy(false); }
  };

  const logCar = async () => {
    if (!carForm.car_type.trim()) { toast.error(t.carTypeRequired); return; }
    if (carForm.plateLetters.length !== 3 || carForm.plateNumbers.length !== 4) {
      toast.error(t.plateInvalid); return;
    }
    setSavingCar(true);
    try {
      const res = await axios.post(`${API}/valet/public/cars`, {
        code: code.trim(),
        car_type: carForm.car_type,
        color: carForm.color,
        plate: `${carForm.plateLetters} ${carForm.plateNumbers}`,
      });
      setSession((s) => ({ ...s, cars: [res.data, ...(s.cars || [])] }));
      setCarForm({ car_type: "", color: "", plateLetters: "", plateNumbers: "" });
      setOtherBrand(false);
      toast.success(t.carSaved);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.carFail);
    } finally { setSavingCar(false); }
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen bg-gradient-to-b from-[#655dc6] to-[#4a3fa8] py-8 px-4">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex justify-end">
          <button
            onClick={() => setLang(isRTL ? "en" : "ar")}
            className="flex items-center gap-1.5 text-white/90 hover:text-white text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-1.5 transition-colors"
            data-testid="valet-lang-toggle"
          >
            <Globe className="w-4 h-4" /> {t.langBtn}
          </button>
        </div>
        <div className="text-center text-white mb-6">
          <Car className="w-12 h-12 mx-auto mb-2" />
          <h1 className="text-2xl font-bold">{t.title}</h1>
          <p className="text-white/70 text-sm">{t.center}</p>
        </div>

        {!session ? (
          <Card className="border-0 shadow-xl">
            <CardContent className="py-8 px-6 space-y-4">
              <p className="text-center text-slate-600 text-sm">{t.enterCode}</p>
              <Input
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 8)); setError(""); }}
                placeholder="••••" inputMode="numeric" maxLength={8}
                className="text-center text-3xl tracking-[0.5em] font-bold h-16"
                onKeyDown={(e) => e.key === "Enter" && checkin()}
                data-testid="valet-code-input"
              />
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="valet-error">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-xs text-red-600 font-medium">{error}</p>
                </div>
              )}
              <Button onClick={checkin} disabled={busy} className="w-full h-12 text-lg bg-[#655dc6] hover:bg-[#5349b8] text-white" data-testid="valet-checkin-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : t.checkin}
              </Button>
              <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3" /> {t.geoHint}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-0 shadow-xl">
              <CardContent className="py-5 px-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-lg text-[#4a3fa8]" data-testid="valet-driver-name">{session.driver.name}</p>
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {t.presentSince} {fmtTime(session.shift.checkin_at)}
                    </p>
                  </div>
                  <Button variant="outline" onClick={checkout} disabled={busy} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid="valet-checkout-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogOut className="w-4 h-4" /> {t.checkout}</>}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl">
              <CardContent className="py-5 px-6 space-y-3">
                <h2 className="font-bold text-[#4a3fa8] flex items-center gap-2"><Car className="w-5 h-5" /> {t.logCarTitle}</h2>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">{t.carType}</label>
                  <div className="grid grid-cols-4 gap-1.5 max-h-56 overflow-y-auto pr-1" data-testid="brand-grid">
                    {brands.map((b) => {
                      const selected = !otherBrand && carForm.car_type === b.name;
                      return (
                        <button key={b.name} type="button"
                          onClick={() => { setCarForm({ ...carForm, car_type: b.name }); setOtherBrand(false); }}
                          className={`flex flex-col items-center justify-center gap-1 rounded-lg border p-1.5 bg-white transition-all ${selected ? "border-[#655dc6] ring-2 ring-[#655dc6]/40 shadow" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`brand-${b.file}`}>
                          <img src={`/car-logos/${b.file}.png`} alt={b.name} className="h-7 w-full object-contain" loading="lazy" />
                          <span className="text-[9px] font-semibold text-slate-600 leading-none">{b.name}</span>
                        </button>
                      );
                    })}
                    <button type="button"
                      onClick={() => { setOtherBrand(true); setCarForm({ ...carForm, car_type: "" }); }}
                      className={`flex flex-col items-center justify-center gap-1 rounded-lg border p-1.5 bg-white transition-all ${otherBrand ? "border-[#655dc6] ring-2 ring-[#655dc6]/40 shadow" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid="brand-other">
                      <Car className="h-7 w-7 text-slate-400" />
                      <span className="text-[9px] font-semibold text-slate-600 leading-none">{t.otherBrand}</span>
                    </button>
                  </div>
                  {otherBrand && (
                    <Input placeholder={t.otherBrandPlaceholder} value={carForm.car_type}
                      onChange={(e) => setCarForm({ ...carForm, car_type: e.target.value })}
                      className="mt-2" data-testid="car-type-input" />
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">{t.color}</label>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="color-grid">
                    {CAR_COLORS.map((c) => {
                      const selected = carForm.color === c.name;
                      return (
                        <button key={c.name} type="button"
                          onClick={() => setCarForm({ ...carForm, color: selected ? "" : c.name })}
                          className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 bg-white transition-all ${selected ? "border-[#655dc6] ring-2 ring-[#655dc6]/40 shadow" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`color-${c.name.toLowerCase()}`}>
                          <span className="w-5 h-5 rounded-md border border-slate-300 shrink-0" style={{ backgroundColor: c.hex }} />
                          <span className="text-[11px] font-semibold text-slate-600">{c.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">{t.plateLabel}</label>
                  <div className="grid grid-cols-2 gap-2" dir="ltr">
                    <Input placeholder="ABC" value={carForm.plateLetters} maxLength={3}
                      onChange={(e) => setCarForm({ ...carForm, plateLetters: e.target.value.toUpperCase().split("").filter((c) => PLATE_ALLOWED_LETTERS.includes(c)).join("").slice(0, 3) })}
                      className="text-center font-mono font-bold tracking-[0.4em] uppercase" data-testid="car-plate-letters-input" />
                    <Input placeholder="1234" value={carForm.plateNumbers} maxLength={4} inputMode="numeric"
                      onChange={(e) => setCarForm({ ...carForm, plateNumbers: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                      className="text-center font-mono font-bold tracking-[0.4em]" data-testid="car-plate-numbers-input" />
                  </div>
                  {carForm.plateLetters.length === 3 && carForm.plateNumbers.length === 4 && (
                    <div className="flex justify-center mt-2 text-lg">
                      <SaudiPlate plate={`${carForm.plateLetters} ${carForm.plateNumbers}`} />
                    </div>
                  )}
                </div>
                <Button onClick={logCar} disabled={savingCar} className="w-full bg-[#655dc6] hover:bg-[#5349b8] text-white" data-testid="car-submit-btn">
                  {savingCar ? <Loader2 className="w-4 h-4 animate-spin" /> : t.logCarTitle}
                </Button>
              </CardContent>
            </Card>

            {(session.cars || []).length > 0 && (
              <Card className="border-0 shadow-xl">
                <CardContent className="py-5 px-6">
                  <h3 className="font-bold text-sm text-slate-600 mb-3 flex items-center gap-1.5">
                    <Clock className="w-4 h-4" /> {t.shiftCars(session.cars.length)}
                  </h3>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {session.cars.map((c) => (
                      <div key={c.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid={`car-row-${c.id}`}>
                        <div>
                          <span className="font-semibold inline-flex items-center gap-1.5">
                            {brandLogo(c.car_type) && <img src={brandLogo(c.car_type)} alt="" className="h-5 w-8 object-contain" />}
                            {c.car_type}
                          </span>
                          {c.color && (
                            <span className="text-slate-500 inline-flex items-center gap-1"> — {colorHex(c.color) && <span className="w-3 h-3 rounded-sm border border-slate-300 inline-block" style={{ backgroundColor: colorHex(c.color) }} />}{c.color}</span>
                          )}
                          <div className="mt-1 text-base"><SaudiPlate plate={c.plate} /></div>
                        </div>
                        <span className="text-xs text-slate-400">{fmtTime(c.logged_at)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
